import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Alert,
  InputAdornment,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import {
  Edit as EditIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
} from '@mui/icons-material';

const STATUS_OPTIONS = [
  { value: 'PLANNING', label: 'Tervezés alatt' },
  { value: 'CONFIRMED', label: 'Megerősítve' },
  { value: 'COMPLETED', label: 'Lezajlott' },
  { value: 'CANCELLED', label: 'Lemondva' },
];

function SectionHeader({ icon, label }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '8px',
        bgcolor: alpha(theme.palette.secondary.main, 0.2),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 15, color: 'primary.dark' } })}
      </Box>
      <Typography variant="overline" sx={{ color: 'primary.dark', fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
      <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
    </Box>
  );
}

function EditWeddingEventDialog({ open, onClose, onSubmit, event }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    eventDate: null,
    eventTime: null,
    status: 'PLANNING',
    estimatedGuests: '',
    totalBudget: '',
    venueName: '',
    venueAddress: '',
    venueCity: '',
    venueZipCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && event) {
      let eventTimeDate = null;
      if (event.eventTime) {
        const [h, m] = event.eventTime.split(':');
        eventTimeDate = new Date();
        eventTimeDate.setHours(parseInt(h), parseInt(m), 0, 0);
      }
      setFormData({
        name: event.name || '',
        eventDate: event.eventDate ? new Date(event.eventDate) : null,
        eventTime: eventTimeDate,
        status: event.status || 'PLANNING',
        estimatedGuests: event.estimatedGuests != null ? String(event.estimatedGuests) : '',
        totalBudget: event.totalBudget != null ? String(event.totalBudget) : '',
        venueName: event.venueName || '',
        venueAddress: event.venueAddress || '',
        venueCity: event.venueCity || '',
        venueZipCode: event.venueZipCode || '',
      });
      setError(null);
    }
  }, [open, event]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('Az esemény neve kötelező'); return; }
    if (!formData.eventDate) { setError('Az esemény dátuma kötelező'); return; }

    setLoading(true);
    setError(null);
    try {
      await onSubmit({
        name: formData.name.trim(),
        eventDate: formData.eventDate.toISOString(),
        eventTime: formData.eventTime
          ? `${String(formData.eventTime.getHours()).padStart(2, '0')}:${String(formData.eventTime.getMinutes()).padStart(2, '0')}`
          : null,
        status: formData.status,
        estimatedGuests: formData.estimatedGuests ? parseInt(formData.estimatedGuests) : null,
        totalBudget: formData.totalBudget ? parseFloat(formData.totalBudget) : null,
        venueName: formData.venueName.trim() || null,
        venueAddress: formData.venueAddress.trim() || null,
        venueCity: formData.venueCity.trim() || null,
        venueZipCode: formData.venueZipCode.trim() || null,
      });
    } catch (err) {
      setError(err.message || 'Hiba történt az esemény szerkesztése során');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) { setError(null); onClose(); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Fejléc */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px',
            bgcolor: alpha('#fff', 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <EditIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', lineHeight: 1.2 }}>
              Esemény szerkesztése
            </Typography>
            {event?.name && (
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.7) }}>
                {event.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Alapadatok */}
        <SectionHeader icon={<EventIcon />} label="Alapadatok" />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              label="Esemény neve"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="pl. Anna és Péter Esküvője"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
              <DatePicker
                label="Esemény dátuma"
                value={formData.eventDate}
                onChange={(v) => handleChange('eventDate', v)}
                minDate={new Date()}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
              <TimePicker
                label="Esemény időpontja"
                value={formData.eventTime}
                onChange={(v) => handleChange('eventTime', v)}
                ampm={false}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Státusz</InputLabel>
              <Select
                value={formData.status}
                label="Státusz"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Becsült vendégszám"
              type="number"
              fullWidth
              value={formData.estimatedGuests}
              onChange={(e) => handleChange('estimatedGuests', e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                inputProps: { min: 0 },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Teljes költségvetés"
              type="number"
              fullWidth
              value={formData.totalBudget}
              onChange={(e) => handleChange('totalBudget', e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">Ft</InputAdornment>,
                inputProps: { min: 0, step: 10000 },
              }}
            />
          </Grid>
        </Grid>

        {/* Helyszín */}
        <SectionHeader icon={<LocationIcon />} label="Helyszín" />
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Helyszín neve"
              fullWidth
              value={formData.venueName}
              onChange={(e) => handleChange('venueName', e.target.value)}
              placeholder="pl. Grand Hotel Budapest"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Utca, házszám"
              fullWidth
              value={formData.venueAddress}
              onChange={(e) => handleChange('venueAddress', e.target.value)}
              placeholder="pl. Fő utca 1."
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              label="Város"
              fullWidth
              value={formData.venueCity}
              onChange={(e) => handleChange('venueCity', e.target.value)}
              placeholder="pl. Budapest"
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Irányítószám"
              fullWidth
              value={formData.venueZipCode}
              onChange={(e) => handleChange('venueZipCode', e.target.value)}
              placeholder="1011"
              inputProps={{ maxLength: 4 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: 'text.secondary' }}>
          Mégse
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ px: 3 }}
        >
          {loading ? 'Mentés...' : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditWeddingEventDialog;
